/**
 * Future SOS Modal — confirmation + batch rescue plan UI (v2).
 *
 * Two phases, both as `.gm-modal-dialog` sheets:
 *   1) openConfirmModal — step-scoped impact preview with one checkbox per
 *      affected assignment (opt-out per slot), collapsible locked-past chip,
 *      sticky action bar. Returns `{ confirmed, excludedIds }`.
 *   2) openBatchPlansModal — each plan is rendered as a card with a human
 *      verdict headline, swaps grouped by day, and a `פרטים ▾` expander
 *      that reveals the full fairness breakdown, per-participant deltas,
 *      and violations. On touch devices the cards form a scroll-snap carousel
 *      with a pager; on pointer devices they stack with only rank #1
 *      expanded. Soft-violation Apply goes through a confirmation step;
 *      infeasibility is surfaced as inline text with a "narrow the window"
 *      action rather than a muted disabled button.
 */

import type { AffectedAssignment, BatchRescuePlan, BatchRescueResult } from '../engine/future-sos';
import type { Participant, Schedule, Task } from '../models/types';
import { operationalDateKey } from '../utils/date-utils';
import { cleanSlotLabel, escAttr, escHtml, fmt, stripDayPrefix } from './ui-helpers';
import { lockBodyScroll, unlockBodyScroll } from './ui-modal';

// ─── Confirmation ────────────────────────────────────────────────────────────

export interface ConfirmContext {
  participantName: string;
  window: { start: Date; end: Date };
  affected: AffectedAssignment[];
  lockedInPast: AffectedAssignment[];
  dayStartHour: number;
  periodStart: Date;
  /** Assignment IDs to start unchecked (e.g. carried over from a prior infeasible attempt). */
  preExcludedIds?: ReadonlySet<string>;
  /** Optional custom header (icon + title). Defaults to the FSOS branding. */
  headerIcon?: string;
  headerTitlePrefix?: string;
  /** Optional override for the "assignments to replace" subtitle. */
  affectedSectionTitle?: string;
  /** Optional preamble HTML rendered above the window sentence. */
  preambleHtml?: string;
}

/** Convert an absolute timestamp to its schedule-day index (1..periodDays),
 *  relative to the schedule's frozen `periodStart` + `dayStartHour`. */
function toDayIndex(d: Date, periodStart: Date, dayStartHour: number): number {
  const shifted = new Date(d.getTime());
  if (shifted.getHours() < dayStartHour) shifted.setDate(shifted.getDate() - 1);
  const baseMidnight = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate()).getTime();
  const shiftedMidnight = new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate()).getTime();
  return Math.floor((shiftedMidnight - baseMidnight) / (24 * 3600 * 1000)) + 1;
}

export interface ConfirmResult {
  confirmed: boolean;
  /** IDs of affected assignments the user opted out of replacing. */
  excludedIds: string[];
}

export function openConfirmModal(ctx: ConfirmContext): Promise<ConfirmResult> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'gm-modal-backdrop';

    const excludedIds = new Set<string>(ctx.preExcludedIds ?? []);

    const affectedHtml =
      ctx.affected.length > 0
        ? renderAffectedChecklist(ctx.affected, ctx.dayStartHour, ctx.periodStart, excludedIds)
        : '';
    const lockedHtml =
      ctx.lockedInPast.length > 0 ? renderLockedChip(ctx.lockedInPast, ctx.dayStartHour, ctx.periodStart) : '';

    const windowSentence = renderWindowSentence(ctx.window, ctx.periodStart, ctx.dayStartHour);

    const headerIcon = ctx.headerIcon ?? '🆘';
    const headerTitlePrefix = ctx.headerTitlePrefix ?? 'אי־זמינות עתידית — השפעה';
    const affectedSectionTitle = ctx.affectedSectionTitle ?? 'שיבוצים שיש להחליף';

    backdrop.innerHTML = `
      <div class="gm-modal-dialog fsos-modal fsos-confirm-v2" role="dialog" aria-modal="true">
        <div class="gm-modal-header">
          <span class="gm-modal-icon">${escHtml(headerIcon)}</span>
          <span class="gm-modal-title">${escHtml(headerTitlePrefix)} · ${escHtml(ctx.participantName)}</span>
        </div>
        ${ctx.preambleHtml ?? ''}
        <div class="fsos-window-sentence">${windowSentence}</div>
        ${
          ctx.affected.length > 0
            ? `<h4 class="profile-sub-title fsos-affected-title">${escHtml(affectedSectionTitle)}</h4><p class="fsos-keep-note">${escHtml(`שיבוץ שתבטל את סימונו יישאר אצל ${ctx.participantName} ולא יוחלף — גם אם הוא בתוך חלון אי־הזמינות.`)}</p>${affectedHtml}`
            : '<p class="gm-modal-body fsos-empty-note">אין שיבוצים חופפים לחלון זה.</p>'
        }
        ${lockedHtml}
        <div class="fsos-confirm-summary" id="fsos-confirm-summary"></div>
        <div class="gm-modal-actions fsos-sticky-actions">
          <button class="btn-primary fsos-confirm-btn">
            ${ctx.affected.length === 0 ? 'רשום חלון בלבד' : 'מצא תוכניות'}
          </button>
          <button class="btn-sm btn-outline fsos-cancel-btn">ביטול</button>
        </div>
      </div>`;

    lockBodyScroll();
    const close = (val: ConfirmResult) => {
      backdrop.remove();
      unlockBodyScroll();
      document.removeEventListener('keydown', onKey);
      resolve(val);
    };

    const summaryEl = backdrop.querySelector('#fsos-confirm-summary') as HTMLElement;
    const confirmBtn = backdrop.querySelector('.fsos-confirm-btn') as HTMLButtonElement;

    const refreshSummary = () => {
      const selectedCount = ctx.affected.length - excludedIds.size;
      if (ctx.affected.length === 0) {
        summaryEl.textContent = '';
        summaryEl.style.display = 'none';
        return;
      }
      summaryEl.style.display = 'block';
      if (selectedCount === 0) {
        summaryEl.textContent = 'בחרת להשאיר את כל השיבוצים. ייתכן שהחלון יירשם בלבד, ללא החלפות.';
        summaryEl.classList.add('fsos-confirm-summary--muted');
      } else {
        summaryEl.textContent = `יוחלפו ${selectedCount} שיבוצים.`;
        summaryEl.classList.remove('fsos-confirm-summary--muted');
      }
      confirmBtn.textContent = selectedCount === 0 ? 'רשום חלון בלבד' : 'מצא תוכניות';
    };

    backdrop.querySelectorAll<HTMLInputElement>('.fsos-affected-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        const id = cb.dataset.assignmentId ?? '';
        if (!id) return;
        if (cb.checked) excludedIds.delete(id);
        else excludedIds.add(id);
        refreshSummary();
      });
    });

    backdrop.querySelectorAll<HTMLElement>('.fsos-locked-toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const panel = toggle.nextElementSibling as HTMLElement | null;
        if (!panel) return;
        const open = panel.hasAttribute('hidden');
        if (open) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', String(open));
      });
    });

    confirmBtn.addEventListener('click', () => close({ confirmed: true, excludedIds: [...excludedIds] }));
    backdrop
      .querySelector('.fsos-cancel-btn')
      ?.addEventListener('click', () => close({ confirmed: false, excludedIds: [] }));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close({ confirmed: false, excludedIds: [] });
    });
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close({ confirmed: false, excludedIds: [] });
    }
    document.addEventListener('keydown', onKey);

    refreshSummary();
    document.body.appendChild(backdrop);
  });
}

function renderWindowSentence(window: { start: Date; end: Date }, periodStart: Date, dayStartHour: number): string {
  const startLabel = fmtDayLabel(window.start, periodStart, dayStartHour);
  const endLabel = fmtDayLabel(window.end, periodStart, dayStartHour);
  if (startLabel === endLabel) {
    return `החלון: <strong>${escHtml(startLabel)}</strong> <span dir="ltr" class="fsos-ltr">${fmt(window.start)}–${fmt(window.end)}</span>`;
  }
  return `החלון: <strong>${escHtml(startLabel)}</strong> <span dir="ltr" class="fsos-ltr">${fmt(window.start)}</span> → <strong>${escHtml(endLabel)}</strong> <span dir="ltr" class="fsos-ltr">${fmt(window.end)}</span>`;
}

function fmtDayLabel(d: Date, periodStart: Date, dayStartHour: number): string {
  return `יום ${toDayIndex(d, periodStart, dayStartHour)}`;
}

function renderAffectedChecklist(
  items: AffectedAssignment[],
  dayStartHour: number,
  periodStart: Date,
  excludedIds: ReadonlySet<string>,
): string {
  const byDay = groupByOperationalDay(items, dayStartHour);
  let html = '<div class="fsos-confirm-groups">';
  for (const [_key, dayItems] of byDay) {
    if (dayItems.length === 0) continue;
    const anchor = dayItems[0].task.timeBlock.start;
    html += `<div class="fsos-affected-day-group"><div class="fsos-affected-day-header">${escHtml(fmtDayLabel(anchor, periodStart, dayStartHour))}</div><ul class="fsos-confirm-list">`;
    for (const it of dayItems) {
      const time = `<span dir="ltr" class="fsos-ltr">${fmt(it.task.timeBlock.start)}–${fmt(it.task.timeBlock.end)}</span>`;
      const cleaned = it.slot.label ? cleanSlotLabel(it.slot.label) : '';
      const slotLabel = cleaned ? ` · ${escHtml(cleaned)}` : '';
      const checkedAttr = excludedIds.has(it.assignment.id) ? '' : ' checked';
      html += `<li>
        <label class="fsos-affected-check">
          <input type="checkbox" class="fsos-affected-checkbox" data-assignment-id="${escAttr(it.assignment.id)}"${checkedAttr}>
          <span class="fsos-affected-name">${escHtml(stripDayPrefix(it.task.name))}${slotLabel}</span>
        </label>
        ${time}
      </li>`;
    }
    html += '</ul></div>';
  }
  html += '</div>';
  return html;
}

function renderLockedChip(items: AffectedAssignment[], dayStartHour: number, periodStart: Date): string {
  const byDay = groupByOperationalDay(items, dayStartHour);
  let panelHtml = '<div class="fsos-locked-panel" hidden>';
  for (const [_key, dayItems] of byDay) {
    if (dayItems.length === 0) continue;
    const anchor = dayItems[0].task.timeBlock.start;
    panelHtml += `<div class="fsos-affected-day-header">${escHtml(fmtDayLabel(anchor, periodStart, dayStartHour))}</div><ul class="fsos-locked-list">`;
    for (const it of dayItems) {
      const time = `<span dir="ltr" class="fsos-ltr">${fmt(it.task.timeBlock.start)}–${fmt(it.task.timeBlock.end)}</span>`;
      const cleaned = it.slot.label ? cleanSlotLabel(it.slot.label) : '';
      const slotLabel = cleaned ? ` · ${escHtml(cleaned)}` : '';
      panelHtml += `<li><span>${escHtml(stripDayPrefix(it.task.name))}${slotLabel}</span>${time}</li>`;
    }
    panelHtml += '</ul>';
  }
  panelHtml += '</div>';
  return `<div class="fsos-locked-section">
    <button type="button" class="fsos-locked-toggle" aria-expanded="false">
      <span class="fsos-locked-chip">🔒 ${items.length} שיבוצים בעבר (נעולים)</span>
      <span class="fsos-locked-arrow">▾</span>
    </button>
    ${panelHtml}
  </div>`;
}

function groupByOperationalDay<T extends AffectedAssignment>(items: T[], dayStartHour: number): Map<string, T[]> {
  const byDay = new Map<string, T[]>();
  for (const item of items) {
    const key = operationalDateKey(item.task.timeBlock.start, dayStartHour);
    let list = byDay.get(key);
    if (!list) {
      list = [];
      byDay.set(key, list);
    }
    list.push(item);
  }
  const sortedKeys = [...byDay.keys()].sort();
  const ordered = new Map<string, T[]>();
  for (const k of sortedKeys) ordered.set(k, byDay.get(k)!);
  return ordered;
}

// ─── Loading overlay ─────────────────────────────────────────────────────────

const FSOS_LOADER_ID = 'fsos-loading-overlay';

/**
 * Show a brief loading overlay while the (synchronous) batch planner runs.
 * Yields a microtask so the overlay actually paints before the main thread
 * blocks on plan computation. Caller must await `closeLoadingOverlay`.
 */
export async function openLoadingOverlay(): Promise<void> {
  closeLoadingOverlay();
  const overlay = document.createElement('div');
  overlay.id = FSOS_LOADER_ID;
  overlay.className = 'optim-overlay fsos-loading-overlay';
  // Non-interactive busy indicator → a status live region, NOT a focus-trapping
  // dialog: there is nothing to interact with (no buttons/ESC), and the planner
  // runs synchronously so the main thread is blocked while it is up. The cube is
  // decorative (hidden from assistive tech); the heading is the announced text.
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.setAttribute('aria-busy', 'true');
  overlay.innerHTML = `
    <div class="optim-card">
      <div class="cube-loader-wrapper optim-cube" aria-hidden="true">
        <div class="cube-loader">
          <div class="cube-cell" style="--cell-color:#4A90D9"></div>
          <div class="cube-cell" style="--cell-color:#E74C3C"></div>
          <div class="cube-cell" style="--cell-color:#F39C12"></div>
          <div class="cube-cell" style="--cell-color:#27AE60"></div>
          <div class="cube-cell" style="--cell-color:#8E44AD"></div>
          <div class="cube-cell" style="--cell-color:#1ABC9C"></div>
          <div class="cube-cell" style="--cell-color:#3498db"></div>
          <div class="cube-cell" style="--cell-color:#e67e22"></div>
          <div class="cube-cell" style="--cell-color:#2ecc71"></div>
        </div>
      </div>
      <h3>מחפש תוכניות החלפה…</h3>
    </div>`;
  document.body.appendChild(overlay);
  // Double-RAF: wait for layout + paint before returning so the synchronous
  // computation that follows doesn't block the loader from ever rendering.
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export function closeLoadingOverlay(): void {
  document.getElementById(FSOS_LOADER_ID)?.remove();
}

// ─── Infeasibility ───────────────────────────────────────────────────────────

export type InfeasibleDecision = 'remove-and-retry' | 'narrow-window' | 'cancel';

export interface InfeasibleContext {
  participantName: string;
  affected: AffectedAssignment[];
  /** Subset of `affected` for which no candidate chain was found at all. */
  unsolvable: AffectedAssignment[];
  /**
   * True when the planner returned best-so-far due to a wall-clock budget
   * cap. In that case "no plan" may not be a true infeasibility — the search
   * just ran out of time. Surface it so the user can choose to narrow rather
   * than assume the batch is unsolvable.
   */
  timedOut: boolean;
  dayStartHour: number;
  periodStart: Date;
  /** Optional custom header (icon + title prefix). Defaults to the FSOS branding. */
  headerIcon?: string;
  headerTitlePrefix?: string;
}

/**
 * Shown instead of the plans modal when no plan is both HC-clean and complete
 * (i.e. every returned plan either violates HC or leaves slots unfilled).
 *
 * Two distinct failure shapes feed this modal:
 *   - **No-chain slots**: assignments in `unsolvable` had zero candidate
 *     chains at any depth — they cannot be filled regardless of the rest.
 *   - **Composition-only failure**: every slot has individual chains, but no
 *     joint composition passes hard constraints. These slots get an amber
 *     "candidate exists but no working combination" treatment — *not* a
 *     green checkmark, because nothing in this modal is actually applyable.
 *
 * Three actions: remove the no-chain items and retry (preferred when any
 * exist), narrow the window, or cancel.
 */
export function openInfeasibleModal(ctx: InfeasibleContext): Promise<InfeasibleDecision> {
  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'gm-modal-backdrop';

    const unsolvableSet = new Set(ctx.unsolvable.map((a) => a.assignment.id));
    const candidateOnly = ctx.affected.filter((a) => !unsolvableSet.has(a.assignment.id));

    const hasUnsolvable = ctx.unsolvable.length > 0;
    const hasCandidateOnly = candidateOnly.length > 0;

    const timeoutHtml = ctx.timedOut
      ? `<div class="fsos-warning-banner fsos-warning-banner--timeout">
          <strong>⏱ החיפוש נקטע בגלל מגבלת זמן.</strong>
          <span>ייתכן שיש תוכנית טובה יותר שלא נבדקה — מומלץ לצמצם את החלון ולנסות שוב.</span>
        </div>`
      : '';

    let explainText: string;
    if (hasUnsolvable && hasCandidateOnly) {
      explainText =
        'לחלק מהשיבוצים לא נמצאה תוכנית החלפה כלל, ושאר השיבוצים — אף שקיימים להם מועמדים בנפרד — לא ניתן לשלב לתוכנית שעומדת באילוצים הקשיחים. ניתן להוציא את השיבוצים שאין להם פתרון ולנסות שוב; אם גם אחר כך לא יימצא שילוב, יש לצמצם ידנית את הרשימה.';
    } else if (hasUnsolvable) {
      explainText = 'לחלק מהשיבוצים לא נמצאה תוכנית החלפה כלל. ניתן להוציא אותם מהרשימה ולנסות שוב.';
    } else {
      explainText =
        'לכל שיבוץ נמצאו מועמדים בנפרד, אך לא נמצא שילוב ביניהם שעומד באילוצים הקשיחים. נסה להוציא חלק מהשיבוצים מהרשימה כדי לאפשר תוכנית.';
    }
    const explainHtml = `<p class="fsos-infeasible-explain">${escHtml(explainText)}</p>`;
    // Honest consequence: opting a slot out of the batch (remove-and-retry) does
    // NOT vacate it — the unavailable focal keeps that shift (its window is
    // hole-punched so HC-3 stays silent). Say so, so the user isn't surprised.
    const keepNoteHtml = `<p class="fsos-keep-note">${escHtml(`שיבוץ שתסיר מהרשימה יישאר אצל ${ctx.participantName} — הוא לא יוחלף.`)}</p>`;

    const unsolvableHtml = hasUnsolvable
      ? `<section class="fsos-infeasible-section fsos-infeasible-section--missing">
          <h4 class="fsos-infeasible-section-title">❌ לא נמצאה תוכנית החלפה (${ctx.unsolvable.length})</h4>
          <p class="fsos-infeasible-section-note">לשיבוצים האלה אין מועמד מתאים בכלל.</p>
          ${renderInfeasibleList(ctx.unsolvable, ctx.dayStartHour, ctx.periodStart)}
        </section>`
      : '';

    const candidateOnlyHtml = hasCandidateOnly
      ? `<section class="fsos-infeasible-section fsos-infeasible-section--candidate">
          <h4 class="fsos-infeasible-section-title">⚠️ קיימים מועמדים אך לא נמצא שילוב תקף (${candidateOnly.length})</h4>
          <p class="fsos-infeasible-section-note">לשיבוצים האלה יש מועמדים בנפרד, אבל המועמדים מתנגשים זה בזה — אי אפשר ליישם אותם יחד מבלי להפר אילוצים.</p>
          ${renderInfeasibleList(candidateOnly, ctx.dayStartHour, ctx.periodStart)}
        </section>`
      : '';

    const retryLabel = hasUnsolvable ? 'הסר את השיבוצים שאין להם פתרון ונסה שוב' : 'חזור והסר שיבוצים ידנית';

    const infHeaderIcon = ctx.headerIcon ?? '⚠️';
    const infHeaderTitlePrefix = ctx.headerTitlePrefix ?? 'לא נמצאה תוכנית החלפה תקפה';

    backdrop.innerHTML = `
      <div class="gm-modal-dialog fsos-modal fsos-infeasible-modal" role="dialog" aria-modal="true">
        <div class="gm-modal-header">
          <span class="gm-modal-icon">${escHtml(infHeaderIcon)}</span>
          <span class="gm-modal-title">${escHtml(infHeaderTitlePrefix)} · ${escHtml(ctx.participantName)}</span>
        </div>
        ${timeoutHtml}
        ${explainHtml}
        ${keepNoteHtml}
        ${unsolvableHtml}
        ${candidateOnlyHtml}
        <div class="gm-modal-actions fsos-sticky-actions">
          <button class="btn-primary fsos-infeasible-retry-btn">${escHtml(retryLabel)}</button>
          <button class="btn-sm btn-outline fsos-infeasible-narrow-btn">צמצם חלון</button>
          <button class="btn-sm btn-outline fsos-infeasible-cancel-btn">ביטול</button>
        </div>
      </div>`;

    lockBodyScroll();
    const close = (decision: InfeasibleDecision) => {
      backdrop.remove();
      unlockBodyScroll();
      document.removeEventListener('keydown', onKey);
      resolve(decision);
    };

    backdrop.querySelector('.fsos-infeasible-retry-btn')?.addEventListener('click', () => close('remove-and-retry'));
    backdrop.querySelector('.fsos-infeasible-narrow-btn')?.addEventListener('click', () => close('narrow-window'));
    backdrop.querySelector('.fsos-infeasible-cancel-btn')?.addEventListener('click', () => close('cancel'));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close('cancel');
    });
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close('cancel');
    }
    document.addEventListener('keydown', onKey);

    document.body.appendChild(backdrop);
  });
}

function renderInfeasibleList(items: AffectedAssignment[], dayStartHour: number, periodStart: Date): string {
  const byDay = groupByOperationalDay(items, dayStartHour);
  let html = '<div class="fsos-confirm-groups">';
  for (const [_key, dayItems] of byDay) {
    if (dayItems.length === 0) continue;
    const anchor = dayItems[0].task.timeBlock.start;
    html += `<div class="fsos-affected-day-group"><div class="fsos-affected-day-header">${escHtml(fmtDayLabel(anchor, periodStart, dayStartHour))}</div><ul class="fsos-infeasible-list">`;
    for (const it of dayItems) {
      const time = `<span dir="ltr" class="fsos-ltr">${fmt(it.task.timeBlock.start)}–${fmt(it.task.timeBlock.end)}</span>`;
      const cleaned = it.slot.label ? cleanSlotLabel(it.slot.label) : '';
      const slotLabel = cleaned ? ` · ${escHtml(cleaned)}` : '';
      html += `<li>
        <span class="fsos-affected-name">${escHtml(stripDayPrefix(it.task.name))}${slotLabel}</span>
        ${time}
      </li>`;
    }
    html += '</ul></div>';
  }
  html += '</div>';
  return html;
}

// ─── Batch Plans ─────────────────────────────────────────────────────────────

/** Structural subset of BatchRescueResult that the plans modal actually consumes.
 *  Lets capability-change reuse the modal without depending on FSOS-specific
 *  fields (`request`, `excludedInWindow`). */
export type BatchPlansResultLike = Pick<
  BatchRescueResult,
  'affected' | 'infeasibleAssignmentIds' | 'plans' | 'timedOut'
>;

export interface BatchPlansContext {
  result: BatchPlansResultLike;
  schedule: Schedule;
  participantName: string;
  onApply: (plan: BatchRescuePlan) => void;
  /** Invoked when the user taps "צמצם חלון" on the infeasibility banner. */
  onNarrowWindow?: () => void;
  /** Optional custom header (icon + title prefix). Defaults to FSOS branding. */
  headerIcon?: string;
  headerTitlePrefix?: string;
}

interface PlanVerdict {
  level: 'excellent' | 'good' | 'fair';
  label: string;
}

function computeVerdict(plan: BatchRescuePlan): PlanVerdict {
  const cd = plan.compositeDelta;
  if (cd >= 5) return { level: 'excellent', label: 'מצוין' };
  if (cd >= 0) return { level: 'good', label: 'טוב' };
  return { level: 'fair', label: 'סביר' };
}

export function openBatchPlansModal(ctx: BatchPlansContext): void {
  const existing = document.getElementById('fsos-plans-backdrop');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.id = 'fsos-plans-backdrop';
  backdrop.className = 'gm-modal-backdrop';

  const pMap = new Map<string, Participant>();
  for (const p of ctx.schedule.participants) pMap.set(p.id, p);
  const taskMap = new Map<string, Task>();
  for (const t of ctx.schedule.tasks) taskMap.set(t.id, t);

  const dayStartHour = ctx.schedule.algorithmSettings.dayStartHour;
  const hasInfeasible = ctx.result.infeasibleAssignmentIds.length > 0;
  const warningHtml = hasInfeasible ? renderInfeasibleWarning(ctx.result) : '';
  const timeoutHtml = ctx.result.timedOut ? renderTimeoutBanner(ctx.result.plans.length > 0) : '';
  const isTouch = document.documentElement.classList.contains('touch-device');

  let plansHtml = '';
  let pagerHtml = '';
  let liveRegionHtml = '';
  if (ctx.result.plans.length === 0) {
    plansHtml = renderNoPlans();
  } else if (isTouch) {
    // Carousel layout: all plans expanded, horizontally scrollable with snap points.
    const cards = ctx.result.plans
      .map((p) =>
        renderBatchPlanCard(p, pMap, taskMap, dayStartHour, ctx.schedule.periodStart, hasInfeasible, {
          // Compact summary by default on touch — plans compare at a glance; the
          // user expands the finalist for the full chain (footer/apply stay visible).
          expanded: false,
        }),
      )
      .join('');
    plansHtml = `<div class="fsos-carousel" role="region" aria-roledescription="קרוסלה" aria-label="תוכניות החלפה" tabindex="-1">${cards}</div>`;
    // Pager + live region only earn their space when there's more than one plan.
    pagerHtml = ctx.result.plans.length > 1 ? renderCarouselPager(ctx.result.plans) : '';
    liveRegionHtml =
      ctx.result.plans.length > 1
        ? '<div class="fsos-sr-only" aria-live="polite" aria-atomic="true"></div>'
        : '';
  } else {
    // Stacked layout: rank #1 expanded, others collapsed to a verdict summary.
    const cards = ctx.result.plans
      .map((p) =>
        renderBatchPlanCard(p, pMap, taskMap, dayStartHour, ctx.schedule.periodStart, hasInfeasible, {
          expanded: p.rank === 1,
        }),
      )
      .join('');
    plansHtml = `<div class="fsos-plans-stack">${cards}</div>`;
  }

  const plansHeaderIcon = ctx.headerIcon ?? '🆘';
  const plansHeaderTitlePrefix = ctx.headerTitlePrefix ?? 'אי זמינות עתידית — תוכניות';

  backdrop.innerHTML = `
    <div class="gm-modal-dialog fsos-modal fsos-plans-v2 ${isTouch ? 'fsos-plans--touch' : ''}" role="dialog" aria-modal="true">
      <div class="gm-modal-header">
        <span class="gm-modal-icon">${escHtml(plansHeaderIcon)}</span>
        <span class="gm-modal-title">${escHtml(plansHeaderTitlePrefix)} · ${escHtml(ctx.participantName)}</span>
      </div>
      ${timeoutHtml}
      ${warningHtml}
      ${pagerHtml}
      ${liveRegionHtml}
      ${plansHtml}
      <div class="gm-modal-actions fsos-sticky-actions">
        <button class="btn-sm btn-outline fsos-close-btn">סגור</button>
      </div>
    </div>`;

  lockBodyScroll();
  let carouselController: CarouselController | null = null;
  let onResize: (() => void) | null = null;
  const close = () => {
    backdrop.remove();
    unlockBodyScroll();
    document.removeEventListener('keydown', onKey);
    carouselController?.disconnect();
    if (onResize) {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    }
    if (_fsosTooltipEl && document.body.contains(_fsosTooltipEl)) {
      _fsosTooltipEl.style.display = 'none';
    }
  };
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (!carouselController) return;
    const tgt = e.target as HTMLElement | null;
    if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const carouselEl = backdrop.querySelector<HTMLElement>('.fsos-carousel');
    const isRtl = carouselEl ? getComputedStyle(carouselEl).direction === 'rtl' : true;
    const i = carouselController.getIndex();
    switch (e.key) {
      // RTL: reading advances leftward, so ArrowLeft → next (higher rank).
      case 'ArrowRight':
        e.preventDefault();
        carouselController.goToPlan(i + (isRtl ? -1 : 1), { focus: true });
        break;
      case 'ArrowLeft':
        e.preventDefault();
        carouselController.goToPlan(i + (isRtl ? 1 : -1), { focus: true });
        break;
      case 'Home':
        e.preventDefault();
        carouselController.goToPlan(0, { focus: true });
        break;
      case 'End':
        e.preventDefault();
        carouselController.goToPlan(Number.MAX_SAFE_INTEGER, { focus: true });
        break;
    }
  }

  backdrop.querySelector('.fsos-close-btn')?.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });

  wireFsosParticipantHover(backdrop, ctx);

  backdrop.querySelectorAll<HTMLButtonElement>('.fsos-narrow-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      close();
      ctx.onNarrowWindow?.();
    });
  });

  // Expand/collapse runs on both platforms now: on touch the cards open compact
  // (so plans compare at a glance) and the toggle re-centres the carousel after
  // the card resizes. The footer (toggle + apply) is a sibling of the header, so
  // footer/apply taps never bubble to the header's collapse handler.
  const toggleAndRecenter = (card: HTMLElement) => {
    togglePlanCard(card);
    if (isTouch) requestAnimationFrame(() => carouselController?.recenter());
  };
  backdrop.querySelectorAll<HTMLElement>('.fsos-plan-collapsed').forEach((header) => {
    header.addEventListener('click', () => {
      const card = header.closest<HTMLElement>('.fsos-plan-card');
      if (card) toggleAndRecenter(card);
    });
  });
  backdrop.querySelectorAll<HTMLElement>('.fsos-plan-toggle').forEach((toggle) => {
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const card = toggle.closest<HTMLElement>('.fsos-plan-card');
      if (card) toggleAndRecenter(card);
    });
  });

  backdrop.querySelectorAll<HTMLButtonElement>('.fsos-apply-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const planId = btn.dataset.planId;
      const plan = ctx.result.plans.find((p) => p.id === planId);
      if (!plan) return;
      close();
      ctx.onApply(plan);
    });
  });

  if (isTouch) {
    const carousel = backdrop.querySelector<HTMLElement>('.fsos-carousel');
    const planCards = carousel
      ? Array.from(carousel.querySelectorAll<HTMLElement>('.fsos-plan-card'))
      : [];
    const total = planCards.length;
    // Stamp per-card ARIA + insert a grab handle in JS (keeps the desktop stack
    // markup byte-identical and avoids threading params through the renderer).
    planCards.forEach((card, i) => {
      card.setAttribute('role', 'group');
      card.setAttribute('aria-roledescription', 'תוכנית');
      card.setAttribute('aria-label', `תוכנית ${i + 1} מתוך ${total}`);
      card.tabIndex = -1;
      card.dataset.planIndex = String(i);
      const grabber = document.createElement('div');
      grabber.className = 'fsos-card-grabber';
      grabber.setAttribute('aria-hidden', 'true');
      card.insertBefore(grabber, card.firstChild);
    });

    if (carousel && total > 0) {
      const prevBtn = backdrop.querySelector<HTMLButtonElement>('.fsos-carousel-prev');
      const nextBtn = backdrop.querySelector<HTMLButtonElement>('.fsos-carousel-next');
      const counterEl = backdrop.querySelector<HTMLElement>('.fsos-carousel-counter');
      const liveEl = backdrop.querySelector<HTMLElement>('.fsos-sr-only');

      const updateChrome = (i: number) => {
        if (counterEl) counterEl.textContent = `${i + 1} / ${total}`;
        if (prevBtn) prevBtn.disabled = i <= 0;
        if (nextBtn) nextBtn.disabled = i >= total - 1;
        if (liveEl) liveEl.textContent = `תוכנית ${i + 1} מתוך ${total}`;
      };

      carouselController = wireCarouselGestureForwarding(carousel, updateChrome);

      prevBtn?.addEventListener('click', () =>
        carouselController?.goToPlan(carouselController.getIndex() - 1, { focus: true }),
      );
      nextBtn?.addEventListener('click', () =>
        carouselController?.goToPlan(carouselController.getIndex() + 1, { focus: true }),
      );

      // Initialise chrome before append (the IntersectionObserver is async).
      updateChrome(0);

      // Re-centre the active card after orientation/resize (card widths recompute).
      let resizeRaf = 0;
      onResize = () => {
        cancelAnimationFrame(resizeRaf);
        resizeRaf = requestAnimationFrame(() => carouselController?.recenter());
      };
      window.addEventListener('resize', onResize);
      window.addEventListener('orientationchange', onResize);
    }
  }

  document.addEventListener('keydown', onKey);
  document.body.appendChild(backdrop);
}

// Mobile browsers don't reliably chain horizontal swipes from a vertically-
// scrolling child (card body) to a horizontally-scrolling parent (carousel).
// Take over touch handling at the carousel level: lock to an axis after the
// first 8px of movement, then drive carousel.scrollLeft (horizontal) or the
// hit body's scrollTop (vertical) manually. Scroll-snap is disabled during
// the gesture to avoid fighting programmatic scroll, then re-engaged via a
// JS snap on touchend.
// Axis-lock tuning. Vertical (reading the dense swap list) is the dominant
// in-card action, so it is biased: horizontal paging must be clearly intended
// (higher threshold + a dominance ratio). Ambiguous near-diagonal movement is
// deferred rather than committed on the first few pixels (pre-v4 locked at 8px
// on the first move, which mis-committed ~30% of the time on the swap rows).
const CAROUSEL_LOCK_MIN = 10;
const CAROUSEL_X_COMMIT = 16;
const CAROUSEL_Y_COMMIT = 12;
const CAROUSEL_X_DOMINANCE = 1.3;

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Controller returned by wireCarouselGestureForwarding so the modal's keyboard
 * handler, pager buttons, and close() can drive / observe the carousel without
 * module globals. `goToPlan` scrolls + (optionally) focuses a card; the active
 * index is reported back via the onActiveChange callback (driven by swipe,
 * trackpad/free scroll via IntersectionObserver, and rest-state geometry).
 */
interface CarouselController {
  goToPlan: (index: number, opts?: { focus?: boolean }) => void;
  getIndex: () => number;
  recenter: () => void;
  disconnect: () => void;
}

// Mobile browsers don't reliably chain horizontal swipes from a vertically-
// scrolling child (card body) to a horizontally-scrolling parent (carousel).
// Take over touch handling at the carousel level: lock to an axis once the
// movement is clearly directional, then drive carousel.scrollLeft (horizontal)
// or the hit body's scrollTop (vertical) manually. Scroll-snap is disabled
// during the gesture to avoid fighting programmatic scroll, then re-engaged via
// a JS snap on touchend. An IntersectionObserver keeps the active index (and
// thus the pager chrome) in sync for swipe / trackpad / button / key input.
function wireCarouselGestureForwarding(
  carousel: HTMLElement,
  onActiveChange: (index: number) => void,
): CarouselController {
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let axis: 'x' | 'y' | null = null;
  let activeBody: HTMLElement | null = null;
  let verticalPossible = false;
  let forceX = false;
  // Read at gesture time (touchstart), not now: this wiring runs before the
  // backdrop is appended, when getComputedStyle on the still-detached carousel
  // would report 'ltr' and invert the RTL scroll direction.
  let isRtl = false;
  const cards = Array.from(carousel.querySelectorAll<HTMLElement>('.fsos-plan-card'));
  let currentIndex = 0;

  // The ONLY mutator of pager chrome. Does not scroll and does not move focus,
  // so the observer can call it on every swipe frame without side effects.
  const setActiveIndex = (i: number, force = false): void => {
    const clamped = Math.max(0, Math.min(cards.length - 1, i));
    if (clamped === currentIndex && !force) return;
    currentIndex = clamped;
    onActiveChange(clamped);
  };

  // Which card sits nearest the carousel centre — authoritative at rest.
  const nearestCardIndex = (): number => {
    if (cards.length === 0) return 0;
    const rect = carousel.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    let closest = 0;
    let minDist = Infinity;
    cards.forEach((c, i) => {
      const r = c.getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - center);
      if (d < minDist) {
        minDist = d;
        closest = i;
      }
    });
    return closest;
  };

  const goToPlan = (index: number, opts?: { focus?: boolean }): void => {
    if (cards.length === 0) return;
    const clamped = Math.max(0, Math.min(cards.length - 1, index));
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    cards[clamped].scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    setActiveIndex(clamped);
    if (opts?.focus) cards[clamped].focus({ preventScroll: true });
  };

  carousel.addEventListener(
    'touchstart',
    (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      startX = lastX = t.clientX;
      startY = lastY = t.clientY;
      axis = null;
      isRtl = getComputedStyle(carousel).direction === 'rtl';
      const target = t.target as HTMLElement | null;
      activeBody = target?.closest<HTMLElement>('.fsos-plan-body') ?? null;
      // Only protect vertical scroll when the body can actually scroll — short
      // plans have no vertical action, so horizontal paging should be easy.
      verticalPossible = !!activeBody && activeBody.scrollHeight > activeBody.clientHeight + 1;
      // The grab handle is an unambiguous "drag to change plan" zone.
      forceX = !!target?.closest('.fsos-card-grabber');
    },
    { passive: true, capture: true },
  );

  carousel.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      const x = t.clientX;
      const y = t.clientY;

      if (axis === null) {
        const dx = Math.abs(x - startX);
        const dy = Math.abs(y - startY);
        if (dx < CAROUSEL_LOCK_MIN && dy < CAROUSEL_LOCK_MIN) return; // jitter → defer
        if (forceX) {
          axis = 'x';
        } else if (!verticalPossible) {
          if (dx >= CAROUSEL_LOCK_MIN && dx >= dy) axis = 'x';
          else if (dy >= CAROUSEL_Y_COMMIT) axis = 'y';
        } else if (dx >= CAROUSEL_X_COMMIT && dx >= dy * CAROUSEL_X_DOMINANCE) {
          axis = 'x';
        } else if (dy >= CAROUSEL_Y_COMMIT && dy >= dx) {
          axis = 'y';
        }
        if (axis === null) return; // still ambiguous (near-diagonal) → defer, do not preventDefault
        if (axis === 'x') carousel.style.scrollSnapType = 'none';
      }

      if (axis === 'x') {
        // In RTL Chrome/Firefox, scrollLeft is 0 at the rightmost position
        // and becomes negative when scrolled left. In LTR it's the reverse.
        const delta = x - lastX;
        carousel.scrollLeft += isRtl ? delta : -delta;
        e.preventDefault();
      } else if (axis === 'y' && activeBody) {
        const delta = y - lastY;
        activeBody.scrollTop -= delta;
        e.preventDefault();
      }
      lastX = x;
      lastY = y;
    },
    { passive: false, capture: true },
  );

  const endGesture = () => {
    if (axis === 'x') {
      carousel.style.scrollSnapType = '';
      // JS snap: scroll the nearest card's start edge into view, then commit it
      // as the authoritative active index (the IntersectionObserver is async).
      const idx = nearestCardIndex();
      const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
      cards[idx]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
      setActiveIndex(idx);
    }
    axis = null;
    activeBody = null;
    forceX = false;
  };
  carousel.addEventListener('touchend', endGesture, { capture: true });
  carousel.addEventListener('touchcancel', endGesture, { capture: true });

  // Continuous active-index tracking for non-gesture scrolls (trackpad, the
  // touchend smooth snap, key/button navigation). The latest ratio per card is
  // retained so a callback reporting only some cards still picks the global max.
  let io: IntersectionObserver | null = null;
  if (typeof IntersectionObserver === 'function' && cards.length > 0) {
    const ratios = new Map<HTMLElement, number>();
    io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target as HTMLElement, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let best = -1;
        let bestRatio = 0;
        cards.forEach((c, i) => {
          const r = ratios.get(c) ?? 0;
          if (r > bestRatio) {
            bestRatio = r;
            best = i;
          }
        });
        if (best >= 0) setActiveIndex(best);
      },
      { root: carousel, threshold: [0.5, 0.6, 0.75, 0.9] },
    );
    for (const c of cards) io.observe(c);
  }

  return {
    goToPlan,
    getIndex: () => currentIndex,
    recenter: () => goToPlan(currentIndex),
    disconnect: () => io?.disconnect(),
  };
}

function togglePlanCard(cardOrHeader: HTMLElement) {
  const card = cardOrHeader.classList.contains('fsos-plan-card')
    ? cardOrHeader
    : (cardOrHeader.closest('.fsos-plan-card') as HTMLElement | null);
  if (!card) return;
  const nowExpanded = !card.classList.contains('fsos-plan-card--expanded');
  card.classList.toggle('fsos-plan-card--expanded', nowExpanded);
  card.querySelector('.fsos-plan-toggle')?.setAttribute('aria-expanded', String(nowExpanded));
}

function renderTimeoutBanner(hasPartialPlans: boolean): string {
  const body = hasPartialPlans
    ? 'חיפוש התוכניות הגיע לתקרת הזמן לפני שסיים. ייתכנו תוכניות טובות יותר שלא נבדקו.'
    : 'חיפוש התוכניות הגיע לתקרת הזמן לפני שנמצאה תוכנית כלשהי.';
  return `<div class="fsos-warning-banner fsos-warning-banner--timeout">
    <strong>⏱ החיפוש נקטע בגלל מגבלת זמן.</strong>
    <span>${escHtml(body)}</span>
    <button type="button" class="fsos-narrow-btn btn-sm btn-outline">חפש שוב עם חלון קטן יותר</button>
  </div>`;
}

function renderInfeasibleWarning(result: BatchPlansResultLike): string {
  const lines: string[] = [];
  for (const aId of result.infeasibleAssignmentIds) {
    const aff = result.affected.find((a) => a.assignment.id === aId);
    if (!aff) continue;
    const time = `<span dir="ltr" class="fsos-ltr">${fmt(aff.task.timeBlock.start)}–${fmt(aff.task.timeBlock.end)}</span>`;
    const cleaned = aff.slot.label ? cleanSlotLabel(aff.slot.label) : '';
    const slotLabel = cleaned ? ` · ${escHtml(cleaned)}` : '';
    lines.push(`<li>${escHtml(stripDayPrefix(aff.task.name))}${slotLabel} — ${time}</li>`);
  }
  return `<div class="fsos-warning-banner fsos-warning-banner--infeasible">
    <strong>⚠️ לא ניתן להחליף את כל השיבוצים.</strong>
    <ul>${lines.join('')}</ul>
    <button type="button" class="fsos-narrow-btn btn-sm btn-outline">חזור וצמצם חלון</button>
  </div>`;
}

function renderNoPlans(): string {
  return `<div class="fsos-empty-state">
    <div class="fsos-empty-state-icon" aria-hidden="true">🔍</div>
    <h4 class="fsos-empty-state-title">לא נמצאו תוכניות החלפה</h4>
    <p class="fsos-empty-state-body">בדקנו את כל האפשרויות ולא הצלחנו להשלים את כל השיבוצים.</p>
    <div class="fsos-empty-state-actions">
      <button type="button" class="fsos-narrow-btn btn-sm btn-outline">צמצם חלון</button>
    </div>
  </div>`;
}

function renderCarouselPager(plans: BatchRescuePlan[]): string {
  const total = plans.length;
  // Prev/next arrows + an "N / M" counter. Glyphs are in source order (prev
  // first), mirroring the day-navigator; flex + RTL reverse them visually.
  // The counter is visual-only — the polite live region carries the semantics.
  return `<div class="fsos-carousel-pager" role="group" aria-label="ניווט בין תוכניות">
    <button type="button" class="fsos-carousel-step fsos-carousel-prev" aria-label="תוכנית קודמת" disabled>‹</button>
    <span class="fsos-carousel-counter" aria-hidden="true" dir="ltr">1 / ${total}</span>
    <button type="button" class="fsos-carousel-step fsos-carousel-next" aria-label="תוכנית הבאה"${total <= 1 ? ' disabled' : ''}>›</button>
  </div>`;
}

/**
 * Per-op-day change distribution (swaps + splitOps) for a plan's collapsed
 * summary chips. Reuses the same day-keying as renderSwapsGroupedByDay so the
 * chip counts and the expanded day groups always agree; Σ counts === changeCount.
 * A swap/split whose task day can't be resolved buckets under "?" (sorted last).
 */
function computeDayDistribution(
  plan: BatchRescuePlan,
  taskMap: Map<string, Task>,
  dayStartHour: number,
  periodStart: Date,
): { label: string; count: number }[] {
  const counts = new Map<number, number>(); // dayIndex → count; -1 = unresolvable
  const bump = (taskId: string) => {
    const start = taskMap.get(taskId)?.timeBlock.start ?? null;
    const di = start ? toDayIndex(start, periodStart, dayStartHour) : -1;
    counts.set(di, (counts.get(di) ?? 0) + 1);
  };
  for (const sw of plan.swaps) bump(sw.taskId);
  for (const op of plan.splitOps ?? []) bump(op.taskId);
  return [...counts.entries()]
    .sort((a, b) => (a[0] === -1 ? 1 : b[0] === -1 ? -1 : a[0] - b[0]))
    .map(([di, count]) => ({ label: di === -1 ? '?' : `יום ${di}`, count }));
}

function renderBatchPlanCard(
  plan: BatchRescuePlan,
  pMap: Map<string, Participant>,
  taskMap: Map<string, Task>,
  dayStartHour: number,
  periodStart: Date,
  infeasible: boolean,
  opts: { expanded: boolean },
): string {
  const verdict = computeVerdict(plan);

  const swapsGrouped = renderSwapsGroupedByDay(plan, pMap, taskMap, dayStartHour, periodStart);

  const detailsHtml = renderPlanDetails(plan, pMap);

  const recommended = plan.rank === 1 && !plan.isPartial;
  const expandedClass = opts.expanded ? ' fsos-plan-card--expanded' : '';
  const partialBadgeHtml = plan.isPartial
    ? `<span class="fsos-partial-badge" title="תוכנית זו ממלאת רק חלק מהשיבוצים ולא ניתנת להחלה">תוכנית חלקית (לא ניתנת להחלה)</span>`
    : '';
  const focalBadgeHtml = plan.focalContinuityExtended
    ? `<span class="fsos-focal-continuity-badge" title="התוכנית משבצת מחדש את המשתתף למשימות אחרות כדי לשמור על העומס שלו">📜 שמרנו על העומס</span>`
    : '';
  // Per-card deep-chain chip — scoped to the plan that actually used a depth-4/5
  // fallback (replaces the old modal-wide banner that mislabelled a shallow
  // recommended #1 as "emergency" whenever ANY lower-ranked plan was deep).
  const fallbackChipHtml = plan.fallbackDepthUsed
    ? `<span class="fsos-fallback-chip" title="התוכנית כוללת שרשרת החלפות עמוקה (עומק ${plan.fallbackDepthUsed}) — כדאי לעיין בפרטים לפני אישור">⚠️ שרשרת עמוקה</span>`
    : '';
  // Terse disruption metric so collapsed (desktop) cards are comparable at a
  // glance and the mobile carousel shows the cost without expanding. swaps +
  // splitOps = number of changes; perParticipantChanges = people touched.
  const changeCount = plan.swaps.length + (plan.splitOps?.length ?? 0);
  const peopleCount = plan.perParticipantChanges.length;
  const disruptionHtml = `<span class="fsos-plan-disruption" title="${peopleCount} ${peopleCount === 1 ? 'איש' : 'אנשים'} · ${changeCount} ${changeCount === 1 ? 'החלפה' : 'החלפות'}">👥 ${peopleCount} · 🔄 ${changeCount}</span>`;
  const dayDist = computeDayDistribution(plan, taskMap, dayStartHour, periodStart);
  const dayDistHtml = dayDist.length
    ? `<div class="fsos-plan-daydist">${dayDist
        .map((d) => `<span class="fsos-daydist-chip">${escHtml(`${d.label}·${d.count}`)}</span>`)
        .join('')}</div>`
    : '';
  // Row C holds the conditional badges; render the wrapper only when at least
  // one is present so an empty row adds no vertical gap.
  const badgesHtml =
    partialBadgeHtml || focalBadgeHtml || fallbackChipHtml
      ? `<div class="fsos-plan-row-c">${partialBadgeHtml}${focalBadgeHtml}${fallbackChipHtml}</div>`
      : '';
  const rankLine = `#${plan.rank}${recommended ? ' ⭐ מומלץ' : ''}`;

  let applyHtml = '';
  if (infeasible) {
    applyHtml = `<div class="fsos-apply-infeasible">לא ניתן להחיל — יש שיבוצים ללא פתרון</div>`;
  } else {
    applyHtml = `<button class="btn-primary fsos-apply-btn" data-plan-id="${escAttr(plan.id)}">✅ החל תוכנית</button>`;
  }

  return `<div class="fsos-plan-card fsos-plan-card--${verdict.level}${recommended ? ' fsos-plan-card--recommended' : ''}${expandedClass}" data-plan-rank="${plan.rank}">
    <header class="fsos-plan-collapsed">
      <div class="fsos-plan-row-a">
        <span class="fsos-plan-rank">${escHtml(rankLine)}</span>
        <span class="fsos-verdict fsos-verdict--${verdict.level}">${escHtml(verdict.label)}</span>
      </div>
      <div class="fsos-plan-row-b">
        ${disruptionHtml}
        ${dayDistHtml}
      </div>
      ${badgesHtml}
    </header>
    <div class="fsos-plan-body">
      ${swapsGrouped}
      ${detailsHtml}
    </div>
    <footer class="fsos-plan-footer">
      <button type="button" class="fsos-plan-toggle" aria-expanded="${opts.expanded ? 'true' : 'false'}" aria-label="הרחב/הסתר פרטים">
        <span class="fsos-plan-toggle-label">פרטים</span>
        <span class="fsos-plan-toggle-arrow">▾</span>
      </button>
      ${applyHtml}
    </footer>
  </div>`;
}

function renderSwapsGroupedByDay(
  plan: BatchRescuePlan,
  pMap: Map<string, Participant>,
  taskMap: Map<string, Task>,
  dayStartHour: number,
  periodStart: Date,
): string {
  interface RenderedSwap {
    timeStart: string;
    timeEnd: string;
    sortKey: number;
    taskName: string;
    slotLabel: string;
    fromName: string;
    toName: string;
    fromPid: string | undefined;
    toPid: string;
    refTaskId: string;
    dayLabel: string;
  }
  const bySwapDayKey = new Map<string, RenderedSwap[]>();

  for (const sw of plan.swaps) {
    const toName = pMap.get(sw.toParticipantId)?.name ?? '???';
    const fromName = sw.fromParticipantId ? (pMap.get(sw.fromParticipantId)?.name ?? '???') : '—';
    const task = taskMap.get(sw.taskId);
    const taskStart = task?.timeBlock.start ?? null;
    const taskEnd = task?.timeBlock.end ?? null;
    const dayKey = taskStart ? operationalDateKey(taskStart, dayStartHour) : sw.assignmentId;
    const dayLabel = taskStart ? fmtDayLabel(taskStart, periodStart, dayStartHour) : '';
    let list = bySwapDayKey.get(dayKey);
    if (!list) {
      list = [];
      bySwapDayKey.set(dayKey, list);
    }
    list.push({
      timeStart: taskStart ? fmt(taskStart) : '',
      timeEnd: taskEnd ? fmt(taskEnd) : '',
      sortKey: taskStart ? taskStart.getTime() : 0,
      taskName: stripDayPrefix(sw.taskName),
      slotLabel: cleanSlotLabel(sw.slotLabel),
      fromName,
      toName,
      fromPid: sw.fromParticipantId || undefined,
      toPid: sw.toParticipantId,
      refTaskId: sw.taskId,
      dayLabel,
    });
  }

  const keys = [...bySwapDayKey.keys()].sort();
  let html = '<div class="fsos-plan-swaps-grouped">';
  for (const k of keys) {
    const items = (bySwapDayKey.get(k) ?? []).slice().sort((a, b) => a.sortKey - b.sortKey);
    if (items.length === 0) continue;
    html += `<div class="fsos-plan-swap-day">`;
    if (items[0].dayLabel) html += `<div class="fsos-plan-swap-day-header">${escHtml(items[0].dayLabel)}</div>`;
    html += '<ul class="fsos-plan-swap-list">';
    for (const it of items) {
      const timeTag = it.timeStart
        ? `<span class="fsos-ltr" dir="ltr">${escHtml(it.timeStart)}–${escHtml(it.timeEnd)}</span>`
        : '';
      const toSpan = `<span class="fsos-participant-hover" data-pid="${escAttr(it.toPid)}" data-plan-id="${escAttr(plan.id)}" data-ref-task-id="${escAttr(it.refTaskId)}"><strong>${escHtml(it.toName)}</strong></span>`;
      const fromSpan = it.fromPid
        ? `<span class="fsos-participant-hover" data-pid="${escAttr(it.fromPid)}" data-plan-id="${escAttr(plan.id)}" data-ref-task-id="${escAttr(it.refTaskId)}">${escHtml(it.fromName)}</span>`
        : escHtml(it.fromName);
      html += `<li>
        <span class="fsos-plan-swap-task">${escHtml(it.taskName)}${it.slotLabel ? ` (${escHtml(it.slotLabel)})` : ''}</span>
        <span class="fsos-plan-swap-meta"><span class="fsos-plan-swap-names">${toSpan} <span class="fsos-plan-swap-arrow" dir="ltr">←</span> ${fromSpan}</span> ${timeTag}</span>
      </li>`;
    }
    html += '</ul></div>';
  }
  // Split-fill section: render any splitOps after the swap groups.
  if (plan.splitOps && plan.splitOps.length > 0) {
    html +=
      '<div class="fsos-plan-splits"><div class="fsos-plan-swap-day-header">פיצולי משבצת</div><ul class="fsos-plan-swap-list">';
    for (const op of plan.splitOps) {
      const parent = taskMap.get(op.taskId);
      const aName = pMap.get(op.fillA.participantId)?.name ?? op.fillA.displayName;
      const bName = pMap.get(op.fillB.participantId)?.name ?? op.fillB.displayName;
      const midDate = new Date(op.midpointMs);
      const timeTag = parent
        ? `<span class="fsos-ltr" dir="ltr">${escHtml(fmt(parent.timeBlock.start))}–${escHtml(fmt(midDate))} / ${escHtml(fmt(midDate))}–${escHtml(fmt(parent.timeBlock.end))}</span>`
        : '';
      html += `<li>
        <span class="fsos-plan-swap-task">${escHtml(op.taskName)} (${escHtml(op.slotLabel)}) <span class="split-badge">½</span></span>
        <span class="fsos-plan-swap-meta"><span class="fsos-plan-swap-names"><strong>${escHtml(aName)}</strong> / <strong>${escHtml(bName)}</strong></span> ${timeTag}</span>
      </li>`;
    }
    html += '</ul></div>';
  }
  html += '</div>';
  return html;
}

// ─── Per-Participant Post-Plan Preview ───────────────────────────────────────

/**
 * Compute the 2-before / reference / 2-after window of a participant's day
 * after the plan's swaps are applied, anchored on `referenceTaskId`. Mirrors
 * `rescue-modal.ts → computePostSwapTasks`; duplicated locally to keep this
 * module decoupled from rescue.
 */
function computePostSwapTasks(
  participantId: string,
  plan: BatchRescuePlan,
  schedule: Schedule,
  referenceTaskId: string,
): Array<{ taskName: string; start: Date; end: Date; isReference: boolean }> {
  const taskMap = new Map<string, Task>();
  for (const t of schedule.tasks) taskMap.set(t.id, t);

  const myAssignmentTaskIds = new Map<string, string>();
  for (const a of schedule.assignments) {
    if (a.participantId === participantId) myAssignmentTaskIds.set(a.id, a.taskId);
  }
  for (const sw of plan.swaps) {
    if (sw.fromParticipantId === participantId) myAssignmentTaskIds.delete(sw.assignmentId);
    if (sw.toParticipantId === participantId) myAssignmentTaskIds.set(sw.assignmentId, sw.taskId);
  }

  const tasks: Array<{ taskName: string; start: Date; end: Date; isReference: boolean }> = [];
  for (const [, taskId] of myAssignmentTaskIds) {
    const task = taskMap.get(taskId);
    if (!task) continue;
    tasks.push({
      taskName: task.name,
      start: task.timeBlock.start,
      end: task.timeBlock.end,
      isReference: taskId === referenceTaskId,
    });
  }
  tasks.sort((a, b) => a.start.getTime() - b.start.getTime());

  let refIdx = tasks.findIndex((t) => t.isReference);
  if (refIdx === -1) {
    // Participant left this task — anchor by the reference task's start time.
    const refTask = taskMap.get(referenceTaskId);
    if (refTask) {
      const anchor = refTask.timeBlock.start.getTime();
      refIdx = tasks.findIndex((t) => t.start.getTime() >= anchor);
    }
    if (refIdx === -1) refIdx = 0;
  }

  const startIdx = Math.max(0, refIdx - 2);
  const endIdx = Math.min(tasks.length, refIdx + 3);
  return tasks.slice(startIdx, endIdx);
}

function buildPostSwapTooltip(
  participantName: string,
  nextTasks: Array<{ taskName: string; start: Date; end: Date; isReference: boolean }>,
  periodStart: Date,
  dayStartHour: number,
): string {
  let html = `<div class="rescue-hover-tt-header">${escHtml(participantName)} — משימות סביב המשבצת אם יוחל</div>`;
  if (nextTasks.length === 0) {
    html += `<div class="rescue-hover-tt-empty">אין משימות קרובות</div>`;
    return html;
  }
  const baseMidnight = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate()).getTime();
  for (let i = 0; i < nextTasks.length; i++) {
    const t = nextTasks[i];
    const shifted = new Date(t.start.getTime());
    if (shifted.getHours() < dayStartHour) shifted.setDate(shifted.getDate() - 1);
    const shiftedMidnight = new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate()).getTime();
    const dIdx = Math.floor((shiftedMidnight - baseMidnight) / (24 * 3600 * 1000)) + 1;
    const dayStr = `יום ${dIdx}`;
    const timeStr = `<span dir="ltr">${fmt(t.start)} – ${fmt(t.end)}</span>`;
    const refClass = t.isReference ? ' rescue-hover-tt-task--ref' : '';
    const refMarker = t.isReference ? ' ◄' : '';
    html += `<div class="rescue-hover-tt-task${refClass}">${i + 1}. ${escHtml(stripDayPrefix(t.taskName))}${refMarker}<span class="rescue-hover-tt-time">${dayStr} ${timeStr}</span></div>`;
  }
  return html;
}

let _fsosTooltipEl: HTMLElement | null = null;
let _fsosTooltipHideTimer: ReturnType<typeof setTimeout> | null = null;

function getFsosTooltipEl(): HTMLElement {
  if (_fsosTooltipEl && document.body.contains(_fsosTooltipEl)) return _fsosTooltipEl;
  const el = document.createElement('div');
  el.className = 'rescue-hover-tt';
  el.style.display = 'none';
  document.body.appendChild(el);
  el.addEventListener('mouseenter', () => {
    if (_fsosTooltipHideTimer) {
      clearTimeout(_fsosTooltipHideTimer);
      _fsosTooltipHideTimer = null;
    }
  });
  el.addEventListener('mouseleave', () => {
    _fsosTooltipHideTimer = setTimeout(() => {
      el.style.display = 'none';
    }, 120);
  });
  _fsosTooltipEl = el;
  return el;
}

function wireFsosParticipantHover(backdrop: HTMLElement, ctx: BatchPlansContext): void {
  const pMap = new Map<string, Participant>();
  for (const p of ctx.schedule.participants) pMap.set(p.id, p);
  const isTouch = document.documentElement.classList.contains('touch-device');
  const dayStartHour = ctx.schedule.algorithmSettings.dayStartHour;
  const periodStart = ctx.schedule.periodStart;

  const resolveContext = (target: HTMLElement) => {
    const pid = target.dataset.pid;
    const planId = target.dataset.planId;
    const refTaskId = target.dataset.refTaskId;
    if (!pid || !planId || !refTaskId) return null;
    const plan = ctx.result.plans.find((p) => p.id === planId);
    const participant = pMap.get(pid);
    if (!plan || !participant) return null;
    return { plan, participant, refTaskId };
  };

  if (isTouch) {
    let expandedKey: string | null = null;
    backdrop.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest('.fsos-participant-hover') as HTMLElement | null;
      if (!target) return;
      e.stopPropagation();
      const resolved = resolveContext(target);
      if (!resolved) return;
      const key = `${target.dataset.pid}|${target.dataset.planId}|${target.dataset.refTaskId}`;
      const existing = backdrop.querySelector('.fsos-inline-preview');
      if (existing) existing.remove();
      if (expandedKey === key) {
        expandedKey = null;
        return;
      }
      expandedKey = key;
      const nextTasks = computePostSwapTasks(resolved.participant.id, resolved.plan, ctx.schedule, resolved.refTaskId);
      const detail = document.createElement('div');
      detail.className = 'fsos-inline-preview task-inline-detail';
      detail.innerHTML = buildPostSwapTooltip(resolved.participant.name, nextTasks, periodStart, dayStartHour);
      target.insertAdjacentElement('afterend', detail);
    });
    return;
  }

  backdrop.addEventListener('mouseover', (e) => {
    const target = (e.target as HTMLElement).closest('.fsos-participant-hover') as HTMLElement | null;
    if (!target) return;
    const resolved = resolveContext(target);
    if (!resolved) return;
    if (_fsosTooltipHideTimer) {
      clearTimeout(_fsosTooltipHideTimer);
      _fsosTooltipHideTimer = null;
    }
    const nextTasks = computePostSwapTasks(resolved.participant.id, resolved.plan, ctx.schedule, resolved.refTaskId);
    const tooltip = getFsosTooltipEl();
    tooltip.innerHTML = buildPostSwapTooltip(resolved.participant.name, nextTasks, periodStart, dayStartHour);
    tooltip.style.display = 'block';

    const rect = target.getBoundingClientRect();
    let left = rect.right + 8;
    let top = rect.top - 4;
    const ttWidth = 260;
    const ttHeight = tooltip.offsetHeight || 140;
    if (left + ttWidth > window.innerWidth) left = rect.left - ttWidth - 8;
    if (top + ttHeight > window.innerHeight) top = window.innerHeight - ttHeight - 8;
    if (top < 4) top = 4;
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  });

  backdrop.addEventListener('mouseout', (e) => {
    const target = (e.target as HTMLElement).closest('.fsos-participant-hover') as HTMLElement | null;
    if (!target) return;
    _fsosTooltipHideTimer = setTimeout(() => {
      const tooltip = getFsosTooltipEl();
      tooltip.style.display = 'none';
    }, 120);
  });
}

function renderPlanDetails(plan: BatchRescuePlan, pMap: Map<string, Participant>): string {
  let changesHtml = '';
  if (plan.perParticipantChanges.length > 0) {
    const nAffected = plan.perParticipantChanges.length;
    const affectedSuffix = nAffected === 1 ? 'משתתף אחד' : `${nAffected} משתתפים`;
    changesHtml = `<h5 class="fsos-plan-section-title">שינויי שיבוץ (${escHtml(affectedSuffix)})</h5><ul class="fsos-plan-changes">`;
    for (const change of plan.perParticipantChanges) {
      const name = pMap.get(change.participantId)?.name ?? '???';
      const added = change.added.length;
      const removed = change.removed.length;
      const parts: string[] = [];
      if (added > 0)
        parts.push(
          `<span class="fsos-plan-change-added">${added === 1 ? 'שיבוץ חדש' : `${added} שיבוצים חדשים`}</span>`,
        );
      if (removed > 0)
        parts.push(
          `<span class="fsos-plan-change-removed">${removed === 1 ? 'שיבוץ הוסר' : `${removed} שיבוצים הוסרו`}</span>`,
        );
      changesHtml += `<li><span class="fsos-plan-change-name">${escHtml(name)}</span><span class="fsos-plan-change-delta">${parts.join(' · ')}</span></li>`;
    }
    changesHtml += '</ul>';
  }

  return changesHtml;
}
